import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from "react";
import { deleteDoc, doc, setDoc } from "firebase/firestore";
import { POSUser } from "../type/POSUser";
import { findPOSUserByPIN, getPOSUsers, POS_USERS_UPDATED_EVENT, subscribeStorePOSUsers, updateLastLogin } from "../../utils/posUserStorage";
import { rememberWorkstation } from "../../utils/pos/orderOrigin";
import { isTauriMode } from "../../utils/environmentDetector";
import { db } from "../../firebase/config";
import { useStore } from "./StoreContext";
import { useAuth } from "./AuthContext";
import { flushOfflineBillsOnLogout } from "../../utils/mevOfflineBatch";

const STATION_NOT_ALLOWED = "STATION_NOT_ALLOWED";
const STAFF_SESSION_KEY = "posStaffSessionAlive";

function staffSessionContinues(): boolean {
  try {
    return sessionStorage.getItem(STAFF_SESSION_KEY) === "1";
  } catch {
    return false;
  }
}

function markStaffSessionAlive(): void {
  try {
    sessionStorage.setItem(STAFF_SESSION_KEY, "1");
  } catch {
    // Ignore storage errors.
  }
}

function userAllowedOnStation(user: POSUser, stationId?: string) {
  const ids = Array.isArray(user.stationIds) ? user.stationIds.map(String).filter(Boolean) : [];
  if (!ids.length) return true;
  return Boolean(stationId) && ids.includes(String(stationId));
}

export const linkedPOSUserId = (store: any, email?: string | null) => {
  if (!store || !email) return "";
  const map = store.linkedPOSUserByEmail || {};
  return map[email.toLowerCase()] || "";
};

interface POSUserContextType {
  currentPOSUser: POSUser | null;
  loginPOSUser: (pin: string) => Promise<boolean>;
  loginPOSUserById: (posUserId: string) => Promise<boolean>;
  logoutPOSUser: () => Promise<void>;
  skipLinkedAutoLogin: boolean;
  staffGateReady: boolean;
  isPOSUserLoggedIn: boolean;
  requirePOSUser: () => boolean;
  setCurrentPOSUser: (user: POSUser | null) => void;
}

export const POSUserContext = createContext<POSUserContextType | undefined>(undefined);

const STORAGE_KEY = "currentPOSUser";

interface Props {
  children: ReactNode;
}

export const linkedPOSUserStorageKey = (storeId?: string | null, email?: string | null) => {
  if (!storeId || !email) return "";
  return `linkedPOSUser_${storeId}_${email.toLowerCase()}`;
};

export const readStoredLinkedPOSUserId = (storeId?: string | null, email?: string | null) => {
  const key = linkedPOSUserStorageKey(storeId, email);
  if (!key) return "";
  try {
    return localStorage.getItem(key) || "";
  } catch {
    return "";
  }
};

export const writeStoredLinkedPOSUserId = (
  storeId?: string | null,
  email?: string | null,
  posUserId?: string
) => {
  const key = linkedPOSUserStorageKey(storeId, email);
  if (!key) return;
  try {
    if (posUserId) localStorage.setItem(key, posUserId);
    else localStorage.removeItem(key);
  } catch {
    // Ignore storage errors.
  }
};

export const readKeypadPopupAtLogin = (storeId?: string | null, store?: any) => {
  if (store && typeof store.keypadPopupAtLogin === "boolean") {
    return store.keypadPopupAtLogin;
  }
  if (store) {
    return true;
  }
  try {
    const saved = localStorage.getItem(
      storeId ? `keypadPopupAtLogin_${storeId}` : "keypadPopupAtLogin"
    );
    if (saved === "false") return false;
    if (saved === "true") return true;
  } catch {
    // Ignore storage errors.
  }
  return false;
};

const canAutoLoginLinkedPOSUser = (storeId?: string | null, store?: any) => {
  return readKeypadPopupAtLogin(storeId, store) === false;
};

export const POSUserContextProvider = ({ children }: Props) => {
  const [currentPOSUser, setCurrentPOSUserState] = useState<POSUser | null>(null);
  const [staffGateReady, setStaffGateReady] = useState(false);
  const [posUserStoreId, setPosUserStoreId] = useState("");
  const [skipLinkedAutoLogin, setSkipLinkedAutoLogin] = useState(false);
  const skipAutoLoginRef = useRef(false);
  const coldStartRef = useRef(!staffSessionContinues());
  const coldStartHandledRef = useRef(false);
  const previousStoreIdRef = useRef("");
  const stationLoginRef = useRef<{ storeId: string; stationId: string } | null>(null);
  const loginGenerationRef = useRef(0);
  const { selectedStoreId, currentStore } = useStore();
  const { currentUser } = useAuth();

  useEffect(() => {
    markStaffSessionAlive();
  }, []);

  // Listen for logout events from components outside the provider (like CustomTitleBar)
  useEffect(() => {
    const handleLogoutEvent = () => {
      console.log('[POS Logout debug] POSUserContext: Received logout event');
      console.log('[POS Logout debug] POSUserContext: Clearing currentPOSUser state');
      skipAutoLoginRef.current = true;
      setSkipLinkedAutoLogin(true);
      setCurrentPOSUserState(null);
      loginGenerationRef.current += 1;
      const stationLogin = stationLoginRef.current;
      stationLoginRef.current = null;
      if (stationLogin) {
        deleteDoc(doc(db, "stores", stationLogin.storeId, "stationLogins", stationLogin.stationId)).catch((error) => {
          console.error("Error clearing station login:", error);
        });
      }
      if (selectedStoreId) {
        const storageKey = `${STORAGE_KEY}_${selectedStoreId}`;
        console.log('[POS Logout debug] POSUserContext: Removing from localStorage:', storageKey);
        localStorage.removeItem(storageKey);
        const afterValue = localStorage.getItem(storageKey);
        console.log('[POS Logout debug] POSUserContext: After removal, localStorage value:', afterValue);
      }
    };

    window.addEventListener('pos-user-logout', handleLogoutEvent);
    console.log('[POS Logout debug] POSUserContext: Registered logout event listener');
    
    return () => {
      window.removeEventListener('pos-user-logout', handleLogoutEvent);
      console.log('[POS Logout debug] POSUserContext: Removed logout event listener');
    };
  }, [selectedStoreId]);

  // A new desktop start does not restore the staff member. A reload of this process may.
  useEffect(() => {
    if (!selectedStoreId) return;
    const storageKey = `${STORAGE_KEY}_${selectedStoreId}`;
    if (coldStartRef.current && !coldStartHandledRef.current) {
      coldStartHandledRef.current = true;
      skipAutoLoginRef.current = true;
      setSkipLinkedAutoLogin(true);
      localStorage.removeItem(storageKey);
      setStaffGateReady(true);
      return;
    }
    if (coldStartRef.current) {
      setStaffGateReady(true);
      return;
    }
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      try {
        const user = JSON.parse(stored);
        setPosUserStoreId(selectedStoreId);
        setCurrentPOSUserState(user);
      } catch (error) {
        console.error('[POS Logout debug] POSUserContext: Error loading POS user from localStorage:', error);
        localStorage.removeItem(storageKey);
      }
    }
    setStaffGateReady(true);
  }, [selectedStoreId]);

  // Save POS user to localStorage when it changes
  useEffect(() => {
    console.log('[POS Logout debug] POSUserContext: Save effect triggered, currentPOSUser:', currentPOSUser);
    console.log('[POS Logout debug] POSUserContext: selectedStoreId:', selectedStoreId);
    if (selectedStoreId) {
      const storageKey = `${STORAGE_KEY}_${selectedStoreId}`;
      if (currentPOSUser) {
        console.log('[POS Logout debug] POSUserContext: Saving user to localStorage:', storageKey);
        localStorage.setItem(storageKey, JSON.stringify(currentPOSUser));
        console.log('[POS Logout debug] POSUserContext: User saved to localStorage');
      } else {
        console.log('[POS Logout debug] POSUserContext: currentPOSUser is null, removing from localStorage:', storageKey);
        localStorage.removeItem(storageKey);
        const afterValue = localStorage.getItem(storageKey);
        console.log('[POS Logout debug] POSUserContext: After removal, localStorage value:', afterValue);
      }
    } else {
      console.log('[POS Logout debug] POSUserContext: No selectedStoreId, skipping localStorage save');
    }
  }, [currentPOSUser, selectedStoreId]);

  // A different store always stops on the PIN screen. A refresh that stays on the same store does not.
  useEffect(() => {
    const previous = previousStoreIdRef.current;
    previousStoreIdRef.current = selectedStoreId || "";
    if (!selectedStoreId || !previous || previous === selectedStoreId) return;
    try {
      sessionStorage.removeItem("workstationStoreSwitch");
    } catch {
      // The PIN screen does not depend on this flag.
    }
    skipAutoLoginRef.current = true;
    setSkipLinkedAutoLogin(true);
    setCurrentPOSUserState(null);
    localStorage.removeItem(`${STORAGE_KEY}_${selectedStoreId}`);
  }, [selectedStoreId]);

  // A connected desktop applies website user changes before the next PIN sign-in.
  useEffect(() => {
    if (!selectedStoreId || !isTauriMode()) return undefined;
    return subscribeStorePOSUsers(selectedStoreId, (users) => {
      window.dispatchEvent(new CustomEvent(POS_USERS_UPDATED_EVENT, {
        detail: { storeId: selectedStoreId, users },
      }));
    });
  }, [selectedStoreId]);

  useEffect(() => {
    const email = currentUser?.email;
    const linkedId =
      linkedPOSUserId(currentStore, email) ||
      readStoredLinkedPOSUserId(selectedStoreId, email);
    const isKdsOnly = !!email
      && !!currentStore?.kdsUsers?.includes(email)
      && currentStore?.createdBy !== email
      && !currentStore?.admins?.includes(email)
      && !currentStore?.members?.includes(email);
    if (
      isKdsOnly ||
      !selectedStoreId ||
      !email ||
      !linkedId ||
      currentPOSUser ||
      skipAutoLoginRef.current ||
      currentStore?.subscriptionStatus === "deactivated" ||
      !canAutoLoginLinkedPOSUser(selectedStoreId, currentStore)
    ) {
      return;
    }

    let cancelled = false;
    getPOSUsers(selectedStoreId).then((users) => {
      if (cancelled || skipAutoLoginRef.current) return;
      const user = users.find((item) => item.id === linkedId && item.isActive !== false);
      if (!user) return;
      writeStoredLinkedPOSUserId(selectedStoreId, email, user.id);
      setCurrentPOSUserState(user);
      rememberWorkstation(selectedStoreId, {
        stationId: currentStore?.stationId,
      });
      updateLastLogin(selectedStoreId, user.id).catch((error) => {
        console.error("Error updating last login:", error);
      });
    }).catch((error) => {
      console.error("Error auto-logging linked POS user:", error);
    });

    return () => {
      cancelled = true;
    };
  }, [selectedStoreId, currentUser?.email, currentStore, currentPOSUser]);

  useEffect(() => {
    const email = currentUser?.email;
    const isKdsOnly = !!email
      && !!currentStore?.kdsUsers?.includes(email)
      && currentStore?.createdBy !== email
      && !currentStore?.admins?.includes(email)
      && !currentStore?.members?.includes(email);
    if (isKdsOnly && currentPOSUser) {
      setCurrentPOSUserState(null);
    }
  }, [currentStore, currentUser?.email, currentPOSUser]);

  useEffect(() => {
    const stationId = currentStore?.stationId;
    if (!selectedStoreId || posUserStoreId !== selectedStoreId || !stationId || !currentPOSUser?.id) return;
    const generation = loginGenerationRef.current + 1;
    loginGenerationRef.current = generation;
    const storeId = selectedStoreId;
    const posUserId = currentPOSUser.id;
    const posUserName = currentPOSUser.name || "";
    stationLoginRef.current = { storeId, stationId };
    setDoc(doc(db, "stores", storeId, "stationLogins", stationId), {
      posUserId,
      posUserName,
      stationId,
    }, { merge: true }).then(() => {
      if (loginGenerationRef.current === generation) return;
      return deleteDoc(doc(db, "stores", storeId, "stationLogins", stationId));
    }).catch((error) => {
      console.error("Error saving station login:", error);
    });
  }, [selectedStoreId, posUserStoreId, currentStore?.stationId, currentPOSUser]);

  const loginPOSUser = useCallback(
    async (pin: string): Promise<boolean> => {
      if (!selectedStoreId) {
        return false;
      }

      try {
        const user = await findPOSUserByPIN(selectedStoreId, pin);
        if (user) {
          if (!userAllowedOnStation(user, currentStore?.stationId)) {
            throw new Error(STATION_NOT_ALLOWED);
          }
          skipAutoLoginRef.current = false;
          setSkipLinkedAutoLogin(false);
          setPosUserStoreId(selectedStoreId);
          setCurrentPOSUserState(user);
          rememberWorkstation(selectedStoreId, {
            stationId: currentStore?.stationId,
          });
          // Update last login timestamp (async, don't wait)
          updateLastLogin(selectedStoreId, user.id).catch((error) => {
            console.error("Error updating last login:", error);
          });
          return true;
        }
        return false;
      } catch (error) {
        if (error instanceof Error && error.message === STATION_NOT_ALLOWED) throw error;
        console.error("Error logging in POS user:", error);
        return false;
      }
    },
    [selectedStoreId, currentStore?.subscriptionStatus, currentStore?.stationId]
  );

  const loginPOSUserById = useCallback(
    async (posUserId: string): Promise<boolean> => {
      if (!selectedStoreId || !posUserId) return false;
      try {
        const users = await getPOSUsers(selectedStoreId);
        const user = users.find((item) => item.id === posUserId && item.isActive !== false);
        if (!user || !userAllowedOnStation(user, currentStore?.stationId)) return false;
        skipAutoLoginRef.current = false;
        setSkipLinkedAutoLogin(false);
        setPosUserStoreId(selectedStoreId);
        setCurrentPOSUserState(user);
        rememberWorkstation(selectedStoreId, {
          stationId: currentStore?.stationId,
        });
        updateLastLogin(selectedStoreId, user.id).catch((error) => {
          console.error("Error updating last login:", error);
        });
        return true;
      } catch (error) {
        console.error("Error logging in POS user by id:", error);
        return false;
      }
    },
    [selectedStoreId, currentStore?.subscriptionStatus, currentStore?.stationId]
  );

  const logoutPOSUser = useCallback(async () => {
    await flushOfflineBillsOnLogout();
    console.log('[POS Logout debug] logoutPOSUser called');
    console.log('[POS Logout debug] selectedStoreId:', selectedStoreId);
    console.log('[POS Logout debug] currentPOSUser before logout:', currentPOSUser);
    console.log('[POS Logout debug] STORAGE_KEY:', STORAGE_KEY);
    
    const storageKey = selectedStoreId ? `${STORAGE_KEY}_${selectedStoreId}` : null;
    console.log('[POS Logout debug] storageKey to remove:', storageKey);
    
    if (storageKey && typeof Storage !== 'undefined' && window.localStorage) {
      const beforeValue = window.localStorage.getItem(storageKey);
      console.log('[POS Logout debug] localStorage value before removal:', beforeValue);
    }
    
    skipAutoLoginRef.current = true;
    setSkipLinkedAutoLogin(true);
    loginGenerationRef.current += 1;
    const stationLogin = stationLoginRef.current;
    stationLoginRef.current = null;
    setCurrentPOSUserState(null);
    if (stationLogin) {
      deleteDoc(doc(db, "stores", stationLogin.storeId, "stationLogins", stationLogin.stationId)).catch((error) => {
        console.error("Error clearing station login:", error);
      });
    }
    console.log('[POS Logout debug] setCurrentPOSUserState(null) called');
    
    if (selectedStoreId) {
      const keyToRemove = `${STORAGE_KEY}_${selectedStoreId}`;
      localStorage.removeItem(keyToRemove);
      console.log('[POS Logout debug] Removed from localStorage:', keyToRemove);
      
      // Verify removal
      const afterValue = localStorage.getItem(keyToRemove);
      console.log('[POS Logout debug] localStorage value after removal:', afterValue);
      console.log('[POS Logout debug] Removal successful:', afterValue === null);
    } else {
      console.log('[POS Logout debug] No selectedStoreId, skipping localStorage removal');
    }
    
    console.log('[POS Logout debug] logoutPOSUser completed');
  }, [selectedStoreId, currentPOSUser]);

  const requirePOSUser = useCallback((): boolean => {
    // Check if POS user is required for current operation
    // For now, always require POS user when in POS mode
    // This can be customized based on store settings
    return currentPOSUser !== null;
  }, [currentPOSUser]);

  const setCurrentPOSUser = useCallback((user: POSUser | null) => {
    setCurrentPOSUserState(user);
  }, []);

  const value: POSUserContextType = {
    currentPOSUser,
    loginPOSUser,
    loginPOSUserById,
    logoutPOSUser,
    skipLinkedAutoLogin,
    staffGateReady,
    isPOSUserLoggedIn: currentPOSUser !== null,
    requirePOSUser,
    setCurrentPOSUser,
  };

  return <POSUserContext.Provider value={value}>{children}</POSUserContext.Provider>;
};

export const usePOSUser = (): POSUserContextType => {
  const context = useContext(POSUserContext);
  if (context === undefined) {
    throw new Error("usePOSUser must be used within a POSUserContextProvider");
  }
  return context;
};

// Default export for lazy loading
export default POSUserContextProvider;

